import React, { useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  Divider,
  Skeleton,
  IconButton,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoopIcon from '@mui/icons-material/Loop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MainContainer from 'theme/MainConatiner';
import CustomButton from 'components/Fileds/CustomButton';
import CustomSearch from 'components/Fileds/CustomSearch';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import NoItemsCreated from 'components/NoItemsCreated/NoItemsCreated';
import NoSearchFound from 'components/NoSearchFound/NoSearchFound';
import { CustomPagination } from 'components/TableListing/CommonTable';
import { useNavigate } from 'react-router-dom';
import { CATEGORY_OPTIONS, PRIORITY_OPTIONS } from './CreateTicketScreen';
import { getStatusBadge, getPriorityBadge, formatTimeAgo } from './utils';
import './style.css';

const STATUS_SELECT_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const SupportTicketsContainer = ({
  tickets = [],
  statistics = {},
  totalCount = 0,
  loading = false,
  searchQuery = '',
  setSearchQuery,
  selectedStatus = 'all',
  setSelectedStatus,
  selectedCategory = 'all',
  setSelectedCategory,
  selectedPriority = 'all',
  setSelectedPriority,
  startDate = '',
  setStartDate,
  endDate = '',
  setEndDate,
  selectedSource = 'all',
  setSelectedSource,
  paginationModel,
  setPaginationModel,
  handleClearFilters,
  handleStatCardClick,
  handleCreateClick,
}) => {
  const navigate = useNavigate();
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Compute active filter count
  let activeFilterCount = 0;
  if (selectedStatus !== 'all') activeFilterCount++;
  if (selectedCategory !== 'all') activeFilterCount++;
  if (selectedPriority !== 'all') activeFilterCount++;
  if (startDate) activeFilterCount++;
  if (endDate) activeFilterCount++;
  if (selectedSource !== 'all') activeFilterCount++;

  const statCardsData = [
    { key: 'all', label: 'Total', count: statistics?.all ?? totalCount ?? 0 },
    { key: 'open', label: 'Open', count: statistics?.open ?? 0 },
    { key: 'in_progress', label: 'In Progress', count: statistics?.in_progress ?? 0 },
    { key: 'resolved', label: 'Resolved', count: statistics?.resolved ?? 0 },
    { key: 'closed', label: 'Closed', count: statistics?.closed ?? 0 },
  ];

  return (
    <MainContainer>
      <Box className="support-tickets-card" sx={{ mb: 4 }}>
        {/* Title Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '24px', color: '#111827' }}>
              Support Tickets
            </Typography>
            <Typography variant="body2" sx={{ color: '#737373', fontSize: '14px', mt: 0.5 }}>
              Manage tickets from restaurant admins and riders
            </Typography>
          </Box>
          <CustomButton
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: '10px',
              backgroundColor: '#000000',
              color: '#ffffff',
              px: 3,
              py: 1,
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            New Ticket
          </CustomButton>
        </Box>

        {/* Top Statistics Cards Row (Matching Tables Screen Image 1 - Static Display) */}
        <Box className="ticket-stats-wrapper" mb={2}>
          {statCardsData.map((stat) => (
            <Box key={stat.key} className="ticket-stat-box">
              <Typography className="stat-label">{stat.label}</Typography>
              {loading ? (
                <Skeleton width={30} height={24} />
              ) : (
                <Typography className="stat-count">{stat.count}</Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Horizontal Divider */}
        <Divider sx={{ my: 2.5 }} />

        {/* Search & Collapsible Filter Card Container (Matching Images 2, 3, 4) */}
        <Box className="ticket-filter-card">
          {/* Search Row */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box flexGrow={1}>
              <CustomSearch
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                placeholder="Search by ID, title, or description..."
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <button
                type="button"
                className={`ticket-filter-btn ${isFilterExpanded || activeFilterCount > 0 ? 'active' : ''}`}
                onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              >
                <FilterListIcon fontSize="small" />
                <span>Filters</span>
                {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
              </button>

              {(activeFilterCount > 0 || searchQuery.trim().length > 0) && (
                <span className="ticket-clear-btn" onClick={handleClearFilters}>
                  <CloseIcon fontSize="small" /> Clear
                </span>
              )}
            </Box>
          </Box>

          {/* Collapsible Filter Panel (Images 3 & 4) */}
          <Collapse in={isFilterExpanded} timeout="auto">
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {/* Row 1 Filters */}
              <Grid item xs={12} sm={6} md={2.4}>
                <span className="filter-label">Status</span>
                <CustomSelect
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  options={STATUS_SELECT_OPTIONS}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <span className="filter-label">Category</span>
                <CustomSelect
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  options={[{ value: 'all', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <span className="filter-label">Priority</span>
                <CustomSelect
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  options={[{ value: 'all', label: 'All Priorities' }, ...PRIORITY_OPTIONS]}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <span className="filter-label">From</span>
                <CustomTextField
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="dd-mm-yyyy"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <span className="filter-label">To</span>
                <CustomTextField
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="dd-mm-yyyy"
                />
              </Grid>
            </Grid>
          </Collapse>
        </Box>

        {/* Tickets List */}
        {loading ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
            ))}
          </Box>
        ) : tickets.length === 0 ? (
          searchQuery.trim() || activeFilterCount > 0 ? (
            <NoSearchFound
              title="No tickets match your filter criteria"
              subtitle="Try adjusting or clearing your search and filter options."
            />
          ) : (
            <NoItemsCreated
              title="No Support Tickets Created Yet"
              subtitle="Click '+ New Ticket' to submit your first issue or request."
            />
          )
        ) : (
          <Box display="flex" flexDirection="column">
            {tickets.map((ticket) => {
              const statusBadge = getStatusBadge(ticket.status);
              const priorityBadge = getPriorityBadge(ticket.priority);

              const messages = ticket?.messages || [];
              const validConversationMsgs = messages.filter((m) => !m.is_internal_note && m.message && m.message.trim() !== '');
              const lastMsg = validConversationMsgs.length > 0 ? validConversationMsgs[validConversationMsgs.length - 1] : null;
              const lastMessagePreview = lastMsg?.message || ticket.description || '';

              // Dynamic Awaiting Tag: Based on the last reply's sender
              const isLastFromAdmin = lastMsg
                ? (lastMsg.sender_type === 'super_admin' || lastMsg.sender_type === 'admin' || (lastMsg.sender?.role && lastMsg.sender.role.toLowerCase().includes('admin')))
                : (ticket.last_replied_by === 'admin');

              const isTicketActive = ticket.status === 'open' || ticket.status === 'in_progress' || ticket.status === 'awaiting_vendor' || ticket.status === 'awaiting_admin';

              return (
                <Box
                  key={ticket.id || ticket.ticket_number}
                  className="ticket-list-item-card"
                  onClick={() => navigate(`/support-tickets/${ticket.id || ticket.ticket_number}`)}
                >
                  {/* Top Badges & Meta Info */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      <span className="ticket-number-badge">{ticket.ticket_number || `TKT-${ticket.id}`}</span>

                      <span className={`ticket-pill ${statusBadge.className}`}>
                        {statusBadge.icon}
                        {statusBadge.label}
                      </span>

                      {/* Dynamic Awaiting Tag */}
                      {isTicketActive && (
                        <span className={`ticket-pill ${isLastFromAdmin ? 'status-awaiting_vendor' : 'status-awaiting_admin'}`}>
                          <AccessTimeIcon fontSize="inherit" />
                          {isLastFromAdmin ? 'Awaiting Vendor' : 'Awaiting Admin'}
                        </span>
                      )}

                      <span className={`ticket-pill ${priorityBadge.className}`}>
                        {priorityBadge.label}
                      </span>

                      {ticket.category && (
                        <span className="category-badge">{ticket.category}</span>
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography variant="caption" sx={{ color: '#737373' }}>
                        {formatTimeAgo(ticket.updated_at || ticket.created_at)}
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.2}
                        sx={{
                          color: '#111827',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <span>View</span>
                        <ChevronRightIcon fontSize="small" sx={{ fontSize: 18 }} />
                      </Box>
                    </Box>
                  </Box>

                  {/* Ticket Title / Subject */}
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', color: '#111827', mb: 0.5 }}>
                    {ticket.subject}
                  </Typography>

                  {/* Last Message / Description Preview */}
                  {lastMessagePreview && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#4B5563',
                        fontSize: '14px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {lastMessagePreview}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Custom Pagination Footer */}
        {paginationModel && setPaginationModel && totalCount > 0 && (
          <Box sx={{ mt: 3, pt: 1 }}>
            <CustomPagination
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              total={totalCount}
            />
          </Box>
        )}
      </Box>
    </MainContainer>
  );
};

export default SupportTicketsContainer;